<template>
    <div class="problem-wrapper" :class="{ active: openEdit, inactive: !openEdit, expand: expand }">
        <button class="clickable expand-corner" :title="lang['button.ada_toggle_expand_problem']" :aria-label="lang['button.ada_toggle_expand_problem']" @click="expand = !expand">
            <i class="fas" :class="{'fa-expand' : !expand, 'fa-compress' : expand }"></i>
        </button>

        <div class="flex-c full-h">

            <template v-if="!view">

                <div class="flex-c full-h flex-space flex-stretch">

                    <div class="flex-c flex-stretch">
                        <div class="editor-header flex-stiff flex-space">
                            <div class="flex-c flex-center view-problem-header">{{ command == 'update' ? lang['assignment.edit_problem'] : lang['assignment.new_problem']}}</div>
                            <div class="flex-r flex-stretch">
                                <div class="flex-c flex-center flex-stiff ml-5 mr-2 subtitle">{{ lang['general.problem_name'] }}</div>
                                <input id="problem-name" class="form-control" type="text" v-model="problem.name">
                            </div>

                        </div>

                        <div class="editor-wrapper flex-r flex-stretch">
                            <div class="adaptive-list flex-stiff full-h flex-c" v-if="adaptiveProblems.length > 0">
                                <div class="adaptive-header flex-stiff">Adaptivity</div>
                                <div class="adaptive-tabs flex-stretch">
                                    <div class="adaptive-tab" @click="emitRedrawPath(null, -1)" :class="selectedIndex == -1 ? 'tab-selected' : ''">Initial Problem</div>
                                    <template v-for="(item, index) in adaptiveProblems">
                                        <div class="adaptive-tab" :class="index == selectedIndex ? 'tab-selected' : ''"  >
                                            <div v-html="item.decisionText" @click="emitRedrawPath(item.path, index)" style="width:100%;">{{ item.decisionText }}</div>
                                            <i class="ml-2 mr-1 fal fa-trash-alt" @click="deleteAdaptive(item.id, index)"></i>
                                        </div>
                                    </template>
                                </div>
                            </div>
                            <editor class="flex-stretch" style="border-top:1px solid gray; overflow-y:scroll;"
                                :init="{
                                    theme: 'modern',
                                    skin: 'light',
                                    branding: false,
                                    browser_spellcheck: true,
                                    menubar: false,
                                    resize: false,
                                    statusbar: false,
                                    toolbar_items_size: 'small',
                                    fontsize_formats: '8pt 10pt 11pt 12pt 14pt 18pt 24pt 36pt',
                                    plugins: [
                                        'advlist autolink formula lists link image charmap print preview anchor',
                                        'searchreplace visualblocks code fullscreen textcolor',
                                        'media table contextmenu paste code'
                                    ],
                                    toolbar: [
                                        'undo redo | fontselect fontsizeselect | bold italic underline | forecolor backcolor | bullist numlist | alignleft aligncenter alignright alignjustify | table link image media | code charmap formula'
                                    ],
                                    image_title: true,
                                    automatic_uploads: true,
                                    paste_data_images: true,
                                    images_upload_credentials: true,
                                    file_picker_types: 'image',
                                    images_upload_handler: imageUploadHandler,
                                    file_picker_callback: filePickerCallback,
                                }"
                                v-model="editorText">
                            </editor>
                        </div>
                    </div>

                    <div class="flex-r flex-space mt-3">
                        <div class="flex-r mr-5">
                            <div class="flex-c flex-center mr-3 disabled" title="Correct decision path for this problem"><i class="fal faw fas fa-code-branch fa-rotate-90"></i></div>
                            <div class="d-flex ml-auto path-flag-wrapper" :class="{'disabled': !pathSelect}">
                                <div class="path-flag d-flex w-100" :class="{'has-path-flag': hasPath, 'no-path-flag': !hasPath}" @click="toggleList">
                                    <div class="w-100 text-center">{{currentProblemPath | truncate(34) }}</div>
                                    <div><i v-if="pathSelect" class="fas fa-sort-down"></i></div>
                                </div>
                                <div class="path-list" :class="{'list-open': showPaths, 'list-closed': !showPaths}">
                                    <div v-if="hasPath" class="path-choice" @mouseenter="emitPathHighlight('off')" @click="clearPath">Remove Path</div>
                                    <div v-for="path in paths" class="path-choice" @mouseenter="emitPathHighlight('on', path)" @mouseleave="emitPathHighlight('off')" @click="selectPath(path.id, path)">{{path.name | truncate(42)}}</div>
                                </div>
                            </div>
                        </div>
                        <!--<div class="flex-c flex-center clickable" @click="expand = !expand"><i class="fas" :class="{'fa-expand' : !expand, 'fa-compress' : expand }"></i></div>-->
                        <div class="d-flex">
                            <button v-if="command=='update'" class="btn btn-delete mx-2" @click="kill">{{ lang['button.delete'] }}</button>
                            <button class="btn btn-secondary mx-2" @click="close">{{ lang['button.cancel'] }}</button>
                            <button class="ml-auto btn btn-primary btn-gradient" @click="save">{{ lang['button.save'] }}</button>
                        </div>
                    </div>
                </div>
            </template>

            <template v-if="view">
                    <div class="editor-header flex-stiff">
                        <h4>{{problem.index}}</h4>
                        <div class="d-flex ml-auto">
                            <i v-if="!problem.flagged" class="far fa-bookmark" @click="flagProblem"></i>
                            <i v-else class="fas fa-bookmark flagged" @click="flagProblem"></i>
                            <button class="btn btn-outline mx-2" @click="traverseProblems(-1)">{{ lang['button.previous'] }}</button>
                            <button class="btn btn-outline" @click="traverseProblems(1)">{{ lang['button.next'] }}</button>
                        </div>
                    </div>

                    <div id="problem-text-area" class="editor-main-text flex-stretch" v-html="text">
                        {{text}}
                    </div>
            </template>

            <div v-if="expand" class="ada-nav flex-stiff">
                <div class="flex-r">
                    <div class="flex-c mt-3">
                        <div class="flex-r">
                            <button class="btn btn-outline mr-2" :title="lang['button.ada_restart_problem']" :aria-label="lang['button.ada_restart_problem']" @click="resetProblem()"><i class="far fa-fast-backward"></i></button>
                            <button class="btn btn-outline mr-3" :title="lang['button.ada_previous_decision']" :aria-label="lang['button.ada_previous_decision']" @click="stepBack()"><i class="far fa-caret-left"></i></button>
                        </div>
                    </div>
                    <div class="flex-c flex-center">
                        <div id="ADA-question" class="mt-3 mb-3 large-bold">{{ currentDecision }}</div>
                        <div id="ADA-options">
                            <template v-for="choice in currentChoices">
                                <button class="btn btn-primary mr-2 mt-1" v-if="choice.rID > 0" @click="clickADAChoice(choice.rID, choice.index)">{{ choice.text }}</button>
                                <button class="btn btn-primary mr-2 mt-1" v-if="choice.rID < 0" @click="clickADADecision(choice.rID, choice.index)">{{ choice.text }}</button>
                            </template>
                        </div>
                        <div v-if="showModule" id="ADA-module" class="mt-3" >
                            <button class="btn btn-success" @click="clickADALink(nID)">{{ modulePrompt }}</button>
                        </div>
                    </div>

                </div>

            </div>

        </div>
    </div>

</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import Swal from 'sweetalert2';
export default {
    props: ['is_open','no_problems'],
    data() {
        return {
            currentDecision: "",
            currentChoices: [],
            clickedPath: [],
            showModule: false,
            modulePrompt: "",
            nID: 0,
            adaptiveProblems: [],
            removeAdaptive: false,
            adaptivePath: 0,
            command: '',
            currentProblemPath: "",
            expand: false,
            hasPath: false,
            openEdit: this.is_open && !this.no_problems,
            paths: [],
            pathSelect: false,
            problem: {},
            problemEdit: false,
            selectedIndex: -1,
            showPaths: false,
            success: false,
            text: '',
            view: this.is_open,
            fullTree: fullTree,
            lang: lang,
        }
    },

    components: {
        'editor': Editor
    },

    mounted() {
        bus.$on('addProblem', this.newProblemData);
        bus.$on('editProblem', this.editProblem);
        bus.$on('viewProblem', this.viewProblem);
        bus.$on('closeEditor', this.close);
        bus.$on('addAdaptiveProblem', this.addAdaptiveProblem);
        bus.$on('endPath', this.endPath);
        bus.$on('addPath', this.addPathFromModal);
        bus.$on('changeText', this.changeProblemText);
        bus.$on('clickDecision', this.decisionClicked);
        bus.$on('isAdapted', this.isAdapted);
        bus.$on('loadADAChoice', this.loadADAChoice);
        bus.$on('clickADAChoice', this.clickADAChoice);
        bus.$on('clickADADecision', this.clickADADecision);
        bus.$on('clickADALink', this.clickADALink);
        bus.$on('setADAPath', this.setADAPath);
        bus.$on('resetProblem', this.resetProblem);
        bus.$on('stepBack', this.stepBack);

        if(fullTree != null) {
            let initialNID = fullTree[fullTree.length - 1].nID;
            clickDecision(initialNID, fullTree.length - 1);
        }
    },

    methods: {

        resetProblem() {
            clickDecision(1, 0);
        },

        stepBack() {
            if(this.clickedPath.length < 2){
                this.resetProblem();
            }
            else {
                clickDecision(this.clickedPath[this.clickedPath.length - 1][1], this.clickedPath.length - 1);
            }
        },

        loadADAChoice(node) {
            if(node.type == "terminal"){
                this.currentDecision = node.terminalQuestion;
                this.currentChoices = node.terminalGroup;
            }
            else {
                this.currentDecision = node.text;
                this.currentChoices = node.children;
            }
            this.showModule = (node.showModule && node.module != null);
            if(this.showModule){
                this.modulePrompt = (node.prompt == "" ? lang["model.module_prompt"] : node.prompt);
                this.nID = node.nID;
            }
            if($("#ADA-options").length > 0){
                $("#ADA-options button").first().focus();
            }
        },

        clickADAChoice(rID, index) {
            clickChoice(rID, index);
        },

        clickADADecision(rID, index) {
            if(rID < 0){
                rID = 0 - rID;
            }
            if(!index){
                index = this.clickedPath.length;
            }
            clickDecision(rID, index, "postTerminal");
        },

        clickADALink(nID) {
            clickLink(nID);
        },

        setADAPath(path) {
            this.clickedPath = path;
        },

        changeProblemText(text) {
            if(text == 'root') {
                if(this.text != this.problem.question) {
                    this.text = this.problem.question;
                    $("#problem-text-area").animate({ scrollTop: 0 }, "fast");
                }
            } else {
                if(this.text != text) {
                    this.text = text;
                    $("#problem-text-area").animate({ scrollTop: 0 }, "fast");
                }
            }
            this.$forceUpdate();
        },

        decisionClicked(nID) {
            const index = this.adaptiveProblems.findIndex(x => x.decision_id == nID);
            if(index > -1) {
                this.selectedIndex = index;
            } else {
                this.selectedIndex = -1;
            }
        },

        deleteAdaptive(id, index) {
            const modelId = this.problem.model_id;
            const $this = this;

            if(id) {
                const problemIndex = this.adaptiveProblems.findIndex(x => x.id == id);
                $.ajax({
                    type: 'delete',
                    url: '/models/' + modelId + '/problem/' + this.problem.id + '/adaptive/' +id,
                    success: function (response) {
                        if(response != "error") {
                            $this.selectedIndex = -1;
                            $this.adaptiveProblems.splice(problemIndex, 1);
                            if(!response.adaptive) {
                                $this.problem.adaptive == null;
                            }
                        }
                    }
                });
            } else {
                $this.selectedIndex = -1;
                this.adaptiveProblems.splice(index, 1);
            }
        },

        emitPathHighlight(state, path) {
            if(path) {
                if(state == 'on') {
                    bus.$emit('highlightPath', path);
                    redrawPath(path.path);
                } else if( this.hasPath && this.problem.path_id > 0) {
                    const scenarioPath = this.paths.find(x => x.id == this.problem.path_id);
                    bus.$emit('highlightPath', scenarioPath);
                    redrawPath(scenarioPath.path);
                } else {
                    bus.$emit('removeHighlight');
                    redrawPath(null);
                }
            }
        },

        emitRedrawPath(path, index) {
            const pathToDraw = JSON.parse(path) || null;
            this.selectedIndex = index;
            clickBackground();
            redrawPath(pathToDraw);
        },

        endPath(path, decisionId) {
            const newPath = JSON.stringify(path);
            const pathExists = this.paths.find(pathObj => {
                const pathArray = JSON.stringify(pathObj.path);
                return newPath === pathArray;
            });
            if(pathExists) {
                this.problem.path_id = pathExists.id;
                this.currentProblemPath = pathExists.name;
                this.problemPath(this.problem);
            } else {
                const pathData = {
                    path,
                    decisionId,
                    model_id: this.problem.model_id,
                    invalid : 0,
                }
                bus.$emit('openModal', 'path', pathData);
            }
        },

        toggleList() {
            if(this.pathSelect) {
                this.showPaths = !this.showPaths;
            }
        },

        flagProblem() {
            this.problem.flagged = !this.problem.flagged;
            assignmentObj.questions.forEach(question => {
                if(question.qID == this.problem.id) {
                    question.flagged = this.problem.flagged;
                }
            });
        },

        addAdaptiveProblem(adaptiveData) {
            const { pathArr, decisionText, decisionId:decision_id } = adaptiveData;
            const { id:problem_id, model_id, question:problemQuestion } = this.problem;
            const path = JSON.stringify(pathArr);
            const exists = this.adaptiveProblems.find(x => x.path == path);

            if(!exists) {

                const adaptiveProblemsLength = this.adaptiveProblems.length;

                const question = adaptiveProblemsLength > 0
                    ? this.adaptiveProblems[adaptiveProblemsLength - 1].question
                    : problemQuestion;

                this.adaptiveProblems.push({
                    model_id,
                    problem_id,
                    path,
                    decisionText,
                    question,
                    decision_id,
                });

                this.adaptiveProblems.sort((a,b) => a.path.length - b.path.length);
                const index = this.adaptiveProblems.findIndex(x => x.path == path);

                this.selectedIndex = index;
                $("#adaptButton").addClass("hidden");
                //adaptiveIDs = this.adaptiveProblems.map(item => item.decision_id);
                adaptivePaths = this.adaptiveProblems.map(item => item.path);
            }
        },

        isAdapted(path) {
            let isAdapted = false;
            this.adaptiveProblems.forEach(function(adaptivePath){
                if(JSON.stringify(adaptivePath) == JSON.stringify(path)){
                    isAdapted = true;
                }
            });
            //const isAdapted = this.adaptiveProblems.find(x => x.decision_id == id);
            return isAdapted; // ? true : false;
        },

        newProblemData(data, path, selectPath) {
            flagEditor(true);
            this.openEdit = true;
            closeSidebar();
            this.command = 'create';
            this.selectedIndex = -1;
            var $this = this;

            $.get('/models/' + data.modelId + '/get-new-problem-id', function(response) {
                $this.problem = {
                    id: +response,
                    path_id: data.pathId,
                    model_id: data.modelId,
                    name: '',
                    question: '',
                }

                if(selectPath) {
                    switchToMode('path', true);
                    $this.getPaths($this.problem);
                    $this.pathSelect = true;
                }

                if(path) {
                    $this.currentProblemPath = path.name;
                    $this.pathSelect = false;
                    $this.hasPath = true;
                    redrawPath(path.path);
                    modelMode = 'path';
                }
                bus.$emit('disableButton', 'disable');
			}).catch((error) => {
                bus.$emit('popupError', error);
            });
        },

        clearPath(){
            const id = this.problem.id;
            const path_id = 0;
            const $this = this;

            $.post('/models/' + this.problem.model_id + '/problempath', {
                id: this.problem.id,
                path_id: 0,
            }, function (response) {
                if(!response.error) {
                    $this.currentProblemPath = "No path";
                    $this.problem.path_id = 0;
                    $this.showPaths = false;
                }
            }).catch((error) => {
                bus.$emit('popupError', error);
            });
        },

        editProblem(problem, path, changePath) {
            flagEditor(true);
            this.openEdit = true;
            closeSidebar();
            this.selectedIndex = -1;

            if(changePath) {
                switchToMode('path');
                this.getPaths(problem);
                this.pathSelect = true;
            }
            if(path) {
                this.currentProblemPath = path.name;
                this.pathSelect = changePath;
                modelMode = 'path';
                redrawPath(path.path);
                this.hasPath = true;
            }
            this.problem = problem;

            this.adaptiveProblems = [];
            if(problem.is_adaptive) {
                this.getAdaptiveProblems(problem);
            }

            // this.problem.question = problem.name; //fix this
            this.command = 'update';
            this.problemEdit = true;

            bus.$emit('disableButton', 'disable');
            setTimeout(function() {
            }, 1000);
        },

        getAdaptiveProblems(problem) {
            this.adaptiveProblems = [];
            $.get('/models/' + problem.model_id + '/problem/' + problem.id, (res)=> {
                res.sort((a,b) => a.path.length - b.path.length);
                res.forEach(problem => {
                    problem.path = JSON.stringify(problem.path);
                    this.adaptiveProblems.push(problem);
                    this.adaptivePath = this.problem.path_id;
                    this.problem.is_adaptive = 1;
                });
                adaptivePaths = this.adaptiveProblems.map(item => item.path);
            })
            .catch((error) => {
                bus.$emit('popupError', error);
            });
        },

        viewProblem(problem) {
            this.openEdit = true;
            // closeSidebar();
            this.view = true;
            this.problem = problem;
            updateProblemText(this.problem.id);
            if(problem.adaptive) {
                this.adaptiveProblems = problem.adaptive
                if(problem.complete) {
                    this.text = this.adaptiveProblems[this.adaptiveProblems.length -1].question;
                }
            }
        },

        close() {
            editingProblem = false;//look at binding to problemEditorOpen variable
            this.problemEdit = false; //look at binding to problemEditorOpen variable
            this.openEdit = false;
            openSidebar();
            this.showPaths = false;
            this.currentProblemPath = "";
            this.adaptiveProblems = [];
            this.hasPath = false;
            this.expand = false;
            bus.$emit('disableButton', 'enable');
            flagEditor(false);
            switchToMode('map', true);
        },

        getPaths(problem) {
            var $this = this;
            $.get('/paths/' + problem.model_id, function(response) {
                $this.paths = response;
                $this.currentProblemPath = (problem.path_id > 0) ? $this.problemPath(problem) : 'No path';
            })
            .catch((error) => {
                bus.$emit('popupError', error);
            });
        },

        problemPath(problem) {
            const path = this.paths.find(path => {
                return path.id == problem.path_id;
            });
            return path ? path.name : 'No path';
        },

        save() {
            var $this = this;
            console.log("Saving problem...");
            //this.problem.name = this.problem.question;
            this.adaptiveProblems.forEach(problem => {
                if(!Array.isArray(problem.path)) {
                    problem.path = JSON.parse(problem.path);
                }
            });
            $.post('/models/' + this.problem.model_id + '/problems', {
            	command: this.command,
                id: this.problem.id,
                language: this.problem.language || 'English',
                model_id: this.problem.model_id,
                module: this.problem.module,
                name: this.problem.name,
                path_id: this.problem.path_id,
                question: this.problem.question,
                is_adaptive: this.adaptiveProblems.length > 0 ? 1 : null,
                is_valid: this.problem.is_valid,
                adaptive_problems: this.adaptiveProblems.length > 0 ? this.adaptiveProblems : null,
                remove_adaptive: (this.adaptivePath && this.adaptiveProblems.length == 0) || null
            }, function (response) {
            	if(!response.error) {
                    $this.close();
                    $this.pathSelect
                        ? bus.$emit('refreshDrawer', 'problem')
                        : bus.$emit('updatePathProblems', $this.problem, $this.command);
            	}
            }).catch((error) => {
                bus.$emit('popupError', error);
            });
        },

        kill() {
            var url = "";
            var $this = this;
            var id = this.problem.id;
            var modelId = this.problem.model_id;
            Swal({
                title: lang['general.are_you_sure_delete_problem'],
                text:lang['general.are_you_sure_delete_problem_description'],
                type: 'warning',
                buttonsStyling: false,
                cancelButtonClass: 'btn btn-secondary',
                confirmButtonClass: 'btn btn-delete mr-3',
                confirmButtonText: lang['button.delete'],
                cancelButtonText: lang['button.cancel'],
                focusConfirm: false,
                showCancelButton: true,
            }).then(function(confirm) {
                if(confirm.value) {
                    url = '/models/' + modelId + '/problem/' + id;
                    $.ajax({
                        type: 'delete',
                        url: url,
                        success: function (response) {
                            $this.close();
                            bus.$emit('refreshDrawer', 'problem');
                        }
                    });
                }
            });
        },

        selectPath(id, path) {
            if(this.problem.is_adaptive && id != this.problem.path_id) {
                Swal({
                    title: lang['general.are_you_sure'],
                    text: 'This problem is adaptive. Changing its path will permanently delete all of the adaptive points associated with this problem when the problem is saved. Do you want to continue?',
                    type: 'warning',
                    buttonsStyling: false,
                    cancelButtonClass: 'btn btn-secondary',
                    confirmButtonClass: 'btn btn-delete mr-3',
                    confirmButtonText: lang['button.change'],
                    cancelButtonText: lang['button.cancel'],
                    focusConfirm: false,
                    showCancelButton: true,
                })
                .then(confirm => {
                    if (confirm.value) {
                        this.changeProblemPath(id);
                        this.problem.is_adaptive = false;
                        this.adaptiveProblems = []; //check to see if other adpated array changes accordingly...
                        adaptivePaths = [];
                    } else {
                        this.emitPathHighlight(true, path);
                    }
                });
            } else {
                this.changeProblemPath(id);
            }
        },

        changeProblemPath(id) {
            this.problem.path_id = id;
            const selectedPath = this.paths.find(path => {
                return path.id == id;
            });
            this.currentProblemPath = selectedPath.name;
            if(this.adaptivePath == id) {
                this.getAdaptiveProblems(this.problem);
            }
            this.showPaths = false;
            this.saveProblemPathChange();
        },

        addPathFromModal(id, name) {
            this.problem.path_id = id;
            this.currentProblemPath = name;
            this.saveProblemPathChange();
        },

        saveProblemPathChange(){
            // If we're dealing with an existing problem, the path needs to be auto-saved in the database.
            if(this.command == "update"){
                $.post('/models/' + this.problem.model_id + '/problempath', {
                    id: this.problem.id,
                    path_id: this.problem.path_id,
                }, function (response) {
                    if(!response.error) {
                        // console.log("Path change saved in database");
                    }
                }).catch((error) => {
                    bus.$emit('popupError', error);
                });
            }
        },

        traverseProblems(direction) {
            bus.$emit('traverseProblems', direction);
        },

        imageUploadHandler(blobInfo, success, failure) {
            var formData = new FormData()
            formData.append('file', blobInfo.blob(), blobInfo.filename());
            $.ajax({
                type:'POST',
                url:'/upload-image',
                processData: false,
                contentType: false,
                async: false,
                cache: false,
                data : formData,
                success: function(response) {
                    success(response);
                }
            });
        },

        filePickerCallback(cb, value, meta) {
            var input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');

            input.onchange = function () {
                var file = this.files[0];

                var reader = new FileReader();
                reader.onload = function () {
                    var id = (new Date()).getTime();
                    var blobCache =  tinymce.activeEditor.editorUpload.blobCache;
                    var base64 = reader.result.split(',')[1];
                    var blobInfo = blobCache.create(id, file, base64);
                    blobCache.add(blobInfo);

                    cb(blobInfo.blobUri(), { title: file.name });
                };
                reader.readAsDataURL(file);
            };

            input.click();
        }

    },

    computed: {
        editorText: {
            get() {
                var currentQuestion = this.problem.question;
                if(this.adaptiveProblems.length > 0) {
                    for(let a = 0; a < this.adaptiveProblems.length; a++) {
                        this.adaptiveProblems[a].pathLength = Array.isArray(this.adaptiveProblems[a].path) ? this.adaptiveProblems[a].path.length : JSON.parse(this.adaptiveProblems[a].path).length;
                    }
                    var partialPath = [];
                    for(let s = 0; s < this.clickedPath.length; s++){
                       partialPath.push(this.clickedPath[s]);
                        for(let a = 0; a < this.adaptiveProblems.length; a++){
                           if(this.adaptiveProblems[a].pathLength == partialPath.length && JSON.stringify(partialPath) == this.adaptiveProblems[a].path){
                               currentQuestion = this.adaptiveProblems[a].question;
                               this.selectedIndex = a;
                           }
                       }
                    }
                }
                return currentQuestion;
            },
            set(newVal) {
                if(this.selectedIndex >= 0) {
                    this.adaptiveProblems[this.selectedIndex].question = newVal;
                } else {
                    this.problem.question = newVal;
                }
            }
        }
    },

    watch: {
        currentProblemPath(newVal, oldVal) {
            if(this.currentProblemPath == "No path" || this.currentProblemPath == "") {
                this.hasPath = false;
            } else {
                this.hasPath = true;
            }
        },

        adaptiveProblems(val) {
            //adaptiveIDs = val.map(item => item.decision_id);
            adaptivePaths = val.map(item => item.path);
        },

        'problem': {
            handler(val) {
                if(dbProfiles[val.path_id]){
                    currentPathID = val.path_id;
                }
                else {
                    currentPathID = 0;
                }
            },
            deep: true,
        }
    }
}
</script>


